/**
 * Hytropics DRS System v6.0 - Live Updates Everywhere
 * Updates DRS live on cart page AND drawer without freezing
 */
(function () {
  'use strict';

  // ============================================
  // CONFIG
  // ============================================
  const DEPOSITS = {
    "12": 52191048434007,
    "24": 52191048466775,
    "36": 52191048499543
  };
  
  const DEPOSIT_HANDLE = 'deposit-return-scheme';
  const IS_CART_PAGE = window.location.pathname.includes('/cart');
  
  let syncTimer = null;
  let syncing = false;
  let lastSync = 0;

  // ============================================
  // CORE FUNCTIONS
  // ============================================
  
  function log(msg, data) {
    console.log(`[DRS] ${msg}`, data || '');
  }

  function detectPackSize(item) {
    const text = [
      item.variant_title,
      item.product_title,
      item.title
    ].filter(Boolean).join(' ').toLowerCase();
    
    const match = text.match(/\b(12|24|36)\s*[-\s]?pack/i);
    return match ? match[1] : null;
  }

  function calculateNeededDeposits(cart) {
    const counts = { "12": 0, "24": 0, "36": 0 };
    
    if (!cart.items) return counts;
    
    cart.items.forEach(item => {
      if (item.product_handle === DEPOSIT_HANDLE) return;
      if (Object.values(DEPOSITS).includes(item.variant_id)) return;
      
      const pack = detectPackSize(item);
      if (pack && counts[pack] !== undefined) {
        counts[pack] += item.quantity;
      }
    });
    
    return counts;
  }

  function findDepositLine(cart, variantId) {
    if (!cart.items) return null;
    for (let i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === variantId) {
        return { line: i + 1, qty: cart.items[i].quantity };
      }
    }
    return null;
  }

  async function syncDeposits() {
    const now = Date.now();
    if (now - lastSync < 800) {
      log('Throttled');
      return;
    }
    
    if (syncing) {
      log('Already syncing');
      return;
    }
    
    syncing = true;
    lastSync = now;
    log('=== SYNC START ===');
    
    try {
      await new Promise(r => setTimeout(r, 100));
      
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Cart fetch failed');
      const cart = await res.json();
      
      const needed = calculateNeededDeposits(cart);
      log('Needed:', needed);
      
      let changed = false;
      
      for (const [pack, targetQty] of Object.entries(needed)) {
        const depositId = DEPOSITS[pack];
        const existing = findDepositLine(cart, depositId);
        const currentQty = existing ? existing.qty : 0;
        
        if (currentQty === targetQty) continue;
        
        changed = true;
        
        if (existing) {
          log(`Update ${pack}: ${currentQty} → ${targetQty}`);
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line: existing.line, quantity: targetQty })
          });
        } else if (targetQty > 0) {
          log(`Add ${pack}: ${targetQty}`);
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: depositId, quantity: targetQty })
          });
        }
        
        await new Promise(r => setTimeout(r, 80));
      }
      
      if (changed) {
        await new Promise(r => setTimeout(r, 150));
        
        if (IS_CART_PAGE) {
          // Cart page: Only update subtotal section
          log('Updating cart page subtotal');
          await updateCartPageSubtotal();
        } else {
          // Drawer: Full refresh
          log('Refreshing drawer');
          if (typeof window.refreshCart === 'function') {
            window.refreshCart();
          }
        }
      }
      
      log('=== SYNC END ===');
      
    } catch (err) {
      log('ERROR:', err.message);
    } finally {
      syncing = false;
    }
  }

  async function updateCartPageSubtotal() {
    try {
      const res = await fetch('/cart?view=subtotal');
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSubtotal = doc.querySelector('#AjaxCartSubtotal');
      const currentSubtotal = document.querySelector('#AjaxCartSubtotal');
      
      if (newSubtotal && currentSubtotal) {
        currentSubtotal.innerHTML = newSubtotal.innerHTML;
        log('Cart subtotal updated');
      }
    } catch (err) {
      log('Subtotal update error:', err);
    }
  }

  function scheduleSync(delay = 600) {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncTimer = null;
      syncDeposits();
    }, delay);
    log(`Scheduled in ${delay}ms`);
  }

  // ============================================
  // CART PAGE HANDLERS
  // ============================================
  
  function setupCartPage() {
    log('Cart page - event listener mode only');
    
    // DO NOT listen to cart-updated (causes loop)
    // Only watch for button clicks
    let clickTimeout = null;
    
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.qty-button, .qty-plus, .qty-minus, .remove');
      if (btn) {
        log('Cart page button clicked');
        
        // Clear existing timeout
        if (clickTimeout) clearTimeout(clickTimeout);
        
        // Wait for Krown's update to finish, then sync
        clickTimeout = setTimeout(() => {
          clickTimeout = null;
          syncDeposits();
        }, 800);
      }
    }, true);
  }

  // ============================================
  // DRAWER HANDLERS
  // ============================================
  
  function setupDrawer() {
    log('Drawer - fast auto-sync');
    
    const originalRefreshCart = window.refreshCart;
    
    if (originalRefreshCart) {
      window.refreshCart = function() {
        // Skip if on cart page
        if (IS_CART_PAGE) {
          originalRefreshCart.apply(this, arguments);
          return;
        }
        
        log('Drawer refresh');
        originalRefreshCart.apply(this, arguments);
        scheduleSync(400);
      };
    }
    
    const observer = new MutationObserver((mutations) => {
      const hasChanges = mutations.some(m => 
        m.target.id === 'AjaxCartForm' && m.addedNodes.length > 0
      );
      
      if (hasChanges) {
        log('Drawer items changed');
        scheduleSync(400);
      }
    });
    
    const cartForm = document.getElementById('AjaxCartForm');
    if (cartForm) {
      observer.observe(cartForm, { 
        childList: true, 
        subtree: false,
        attributes: false
      });
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  
  function init() {
    if (typeof KROWN === 'undefined') {
      setTimeout(init, 100);
      return;
    }
    
    log(`DRS v6.0 - ${IS_CART_PAGE ? 'CART PAGE' : 'DRAWER'} mode`);
    
    if (IS_CART_PAGE) {
      setupCartPage();
    } else {
      setupDrawer();
      scheduleSync(800);
    }
    
    log('Ready - Fast live updates');
  }

  // ============================================
  // PUBLIC API
  // ============================================
  
  window.DEPOSIT = {
    sync: () => scheduleSync(300),
    syncNow: syncDeposits,
    version: '6.0-live-updates'
  };

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();